import React, { useState, useMemo, useCallback } from 'react';
import css from './styles.module.scss';
import { Grid, TextField, Button } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { Search } from '~/common/search';
import { DateRange, DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { ChipsFilter } from '~/common/chips-filter';
import moment from 'moment';
import { PaginatedBlock } from '~/features/dashboard/components/paginated-block';
import { DealCard } from '~/features/dashboard/components/deal-card';
import { DealSummaryListDto } from '~/models/deals/DealsListItem.dto';
import { axiosInstance } from '~/api/config/axios.config';
import { useSelector } from 'react-redux';
import { selectContacts } from '~/features/dashboard/store/selectors';
import { User } from '~/store/user/models';
import { useDateFormat } from '~/common/date-format';

interface Props {
    showLinked: boolean;
    items: DealSummaryListDto[];
    onLink(id: string): void;
    onUnlink(id: string): void;
    onLinkAll(): void;
    onUnlinkAll?(): void;
    itemsPerPage?: number;
    contacts?: User[];
}

export const SubDealsRightDrawer: React.FC<Props> = ({
    showLinked,
    items,
    onLink,
    onUnlink,
    onLinkAll,
    onUnlinkAll,
    itemsPerPage = 10,
    contacts,
}) => {
    const { selectedDateFormat } = useDateFormat();
    const [dealTypesFilter, setDealTypesFilter] = useState<string[]>([]);
    const [dealStatusFilter, setDealStatusFilter] = useState<string[]>([]);
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);
    const [searchValue, setSearchValue] = useState('');

    const types = useMemo(() => {
        const dealTypes: string[] = [];
        items.forEach(item => {
            if (item.dealType.trim() && !dealTypes.includes(item.dealType)) {
                dealTypes.push(item.dealType);
            }
        });
        return dealTypes;
    }, [items]);

    const statuses = useMemo(() => {
        const dealStatuses: string[] = [];
        items.forEach(item => {
            if (item.contractType.trim() && !dealStatuses.includes(item.contractType)) {
                dealStatuses.push(item.contractType);
            }
        });
        return dealStatuses;
    }, [items]);

    const filteredItems: DealSummaryListDto[] = useMemo(() => {
        const [minDate, maxDate] = dateFilter;
        if (dealTypesFilter.length || dealStatusFilter.length || searchValue || (minDate && maxDate)) {
            return items.filter(item => {
                if (dealTypesFilter.length && !dealTypesFilter.includes(item.dealType)) {
                    return false;
                }
                if (dealStatusFilter.length && !dealStatusFilter.includes(item.contractType)) {
                    return false;
                }
                if (searchValue) {
                    const title = item.contractTitle.toLowerCase();
                    const search = searchValue.toLowerCase();
                    return title.includes(search);
                }
                if (minDate && maxDate && item.contractStartDate) {
                    const compareDate = moment(item.contractStartDate);
                    compareDate.isSameOrBefore(maxDate) && compareDate.isSameOrAfter(minDate);
                }
                return true;
            });
        }
        return items;
    }, [items, searchValue, dateFilter, dealTypesFilter, dealStatusFilter]);

    // TODO: legalEntityId always 0;
    const getLegalEntityName = useCallback((email?: string): string | undefined => {
        if (!email) return undefined;
        return contacts?.find(c => c.email === email)?.companyName;
    }, [contacts]);

    return (
        <Grid container className={css.module} spacing={3}>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                        {showLinked ? 'VIEW DEALS' : 'ADD DEALS'}
                    </Typography>
                    {showLinked ? (
                        <Button className={css.btnColorGreen} variant="contained" color="secondary" style={{ height: '2rem', borderRadius: '8px' }} onClick={onUnlinkAll}>
                            Unlink All
                        </Button>
                    ) : (
                        <Button className={css.btnColorGreen} variant="contained" color="secondary" style={{ height: '2rem', borderRadius: '8px' }} onClick={onLinkAll}>
                            Link All
                        </Button>
                    )}
                </Box>
            </Grid>

            <Grid item xs={6}>
                {types.length > 1 && (
                    <Box pb={2}>
                        <Box pb={1}>
                            <Typography variant="body2" className={css.bold}>Deal Type filters:</Typography>
                        </Box>
                        <ChipsFilter chips={types} filters={dealTypesFilter} onFilterChange={setDealTypesFilter} className={css.chipFilter} />
                    </Box>
                )}
                {statuses.length > 1 && (
                    <Box pb={2}>
                        <Box pb={1}>
                            <Typography variant="body2" className={css.bold}>Deal status filters:</Typography>
                        </Box>
                        <ChipsFilter chips={statuses} filters={dealStatusFilter} onFilterChange={setDealStatusFilter} className={css.chipFilter} />
                    </Box>
                )}
            </Grid>

            {items.length > itemsPerPage && (filteredItems.length > itemsPerPage || searchValue || dateFilter[0] || dateFilter[1]) && (
                <Grid item xs={6}>
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Box pb={2}>
                            <LocalizationProvider dateAdapter={MomentUtils}>
                                <DateRangePicker
                                    startText="Start Date"
                                    endText="End Date"
                                    value={dateFilter}
                                    inputFormat={selectedDateFormat}
                                    onChange={newValue => setDateFilter(newValue)}
                                    renderInput={(startProps, endProps) => (
                                        <>
                                            <TextField {...startProps} className={css.datePickerInput} variant="standard" />
                                            <DateRangeDelimiter> to </DateRangeDelimiter>
                                            <TextField {...endProps} className={css.datePickerInput} variant="standard" />
                                        </>
                                    )}
                                />
                            </LocalizationProvider>
                        </Box>
                        <Box pb={2}>
                            <Search search={searchValue} onSearchChange={setSearchValue} className={css.search} />
                        </Box>
                    </Box>
                </Grid>
            )}

            <Grid item xs={12}>
                <PaginatedBlock
                    grid={2}
                    keyName="assetCode"
                    data={filteredItems}
                    renderItem={(item, key) => (
                        <DealCard
                            key={key}
                            item={{...item, legalEntityName: getLegalEntityName(item.emailAddressContract)}}
                            onChange={(isChecked): void => {isChecked ? onLink(item.assetCode) : onUnlink(item.assetCode)}}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
};

type ConnectedProps = Omit<Props, 'onLink' | 'onUnlink' | 'onLinkAll' | 'onUnlinkAll'> & {
    assetCode: string;
    projectId: number;
    onClose(): void;
};

const ConnectedSubDealsRightDrawer: React.FC<ConnectedProps> = ({
    assetCode,
    projectId,
    onClose,
    ...props
}) => {

    const onLink = useCallback((id: string): void => {
        axiosInstance
            .post(`catalogue/projectCreator/dealsubdeal/link?mainAssetCode=${assetCode}&subAssetCode=${id}`);
        },
        [assetCode]
    );

    const onUnlink = useCallback((id: string): void => {
        axiosInstance
            .post(`catalogue/projectCreator/dealsubdeal/unlink?mainAssetCode=${assetCode}&subAssetCode=${id}`);
    }, [assetCode]);

    const linkAll = useCallback((): void => {
        axiosInstance
            .post(`catalogue/projectCreator/dealsubdeal/link?ProjectId=${projectId}&mainAssetCode=${assetCode}&all=true`)
            .then(() => { onClose(); })
    }, [projectId, assetCode, onClose]);

    const unlinkAll = useCallback(() => {
        axiosInstance
            .post(`catalogue/projectCreator/dealsubdeal/unlink?ProjectId=${projectId}&mainAssetCode=${assetCode}&all=true`)
            .then(() => { onClose(); })
    }, [projectId, assetCode, onClose]);

    const contacts = useSelector(selectContacts) || [];

    return (
        <SubDealsRightDrawer
            {...props}
            onLink={onLink}
            onUnlink={onUnlink}
            onLinkAll={linkAll}
            onUnlinkAll={unlinkAll}
            contacts={contacts}
        />
    );
}

export default ConnectedSubDealsRightDrawer;
